export const roundMoney = (value) =>
  Math.round(Number(value) * 100) / 100;

export const moneyMatches = (a, b, tolerance = 0.01) =>
  Math.abs(Number(a) - Number(b)) <= tolerance;

export const computeEqualInstallmentAmounts = (netAmount, count) => {
  const net = Number(netAmount);
  const installmentCount = Number(count);

  if (!Number.isFinite(net) || net < 0 || !Number.isFinite(installmentCount) || installmentCount < 1) {
    return [];
  }

  if (installmentCount === 1) {
    return [roundMoney(net)];
  }

  const base = Math.floor((net / installmentCount) * 100) / 100;
  const amounts = Array(installmentCount).fill(base);
  const sumExceptLast = roundMoney(base * (installmentCount - 1));
  amounts[installmentCount - 1] = roundMoney(net - sumExceptLast);
  return amounts;
};

export const getExpectedInstallmentAmount = (netAmount, installmentCount, installmentNumber) => {
  const amounts = computeEqualInstallmentAmounts(netAmount, installmentCount);
  const index = Number(installmentNumber) - 1;
  if (!Number.isFinite(index) || index < 0 || index >= amounts.length) {
    return null;
  }
  return amounts[index];
};

const hasInstallmentAmount = (value) =>
  value !== "" && value !== null && value !== undefined;

export const sumInstallmentAmounts = (installments) => {
  const rows = Array.isArray(installments) ? installments : [];
  return {
    inr: roundMoney(
      rows.reduce((sum, row) => sum + Number(row?.fee_instalment_inr || 0), 0)
    ),
    student: roundMoney(
      rows.reduce(
        (sum, row) =>
          sum + Number(row?.fee_instalment_student_student_currency || 0),
        0
      )
    ),
  };
};

export const buildFeeInstallmentRows = ({
  netInr,
  netStudent,
  count,
  currencyCode,
  existingRows = [],
  preserveAmounts = true,
}) => {
  const installmentCount = Number(count);
  if (!Number.isFinite(installmentCount) || installmentCount < 1) {
    return [];
  }

  const inrAmounts = computeEqualInstallmentAmounts(netInr, installmentCount);
  const studentAmounts = computeEqualInstallmentAmounts(netStudent, installmentCount);

  return Array.from({ length: installmentCount }, (_, index) => {
    const previous = existingRows[index] || {};
    const keepInr =
      preserveAmounts && hasInstallmentAmount(previous?.fee_instalment_inr);
    const keepStudent =
      preserveAmounts &&
      hasInstallmentAmount(previous?.fee_instalment_student_student_currency);

    return {
      installment_number: index + 1,
      due_date: previous?.due_date || "",
      fee_instalment_inr: keepInr
        ? String(previous.fee_instalment_inr)
        : inrAmounts[index] !== undefined
          ? String(inrAmounts[index])
          : "",
      currency_code_inr: previous?.currency_code_inr || "INR",
      fee_instalment_student_student_currency: keepStudent
        ? String(previous.fee_instalment_student_student_currency)
        : studentAmounts[index] !== undefined
          ? String(studentAmounts[index])
          : "",
      student_currency_code:
        currencyCode || previous?.student_currency_code || "",
    };
  });
};

export const validateFeeInstallmentAmounts = ({
  installments,
  netInr,
  netStudent,
}) => {
  const rows = Array.isArray(installments) ? installments : [];
  const count = rows.length;
  if (!count) return null;

  const sortedRows = [...rows].sort(
    (a, b) => Number(a?.installment_number ?? 0) - Number(b?.installment_number ?? 0)
  );

  for (let index = 0; index < sortedRows.length; index += 1) {
    const row = sortedRows[index];
    const installmentNumber = Number(row?.installment_number ?? index + 1);
    const inrValue = Number(row?.fee_instalment_inr);

    if (!Number.isFinite(inrValue) || inrValue < 0) {
      return `Installment #${installmentNumber} amount (INR) must be a valid non-negative number.`;
    }

    const studentRaw = row?.fee_instalment_student_student_currency;
    if (hasInstallmentAmount(studentRaw)) {
      const studentValue = Number(studentRaw);
      if (!Number.isFinite(studentValue) || studentValue < 0) {
        return `Installment #${installmentNumber} amount (student currency) must be a valid non-negative number.`;
      }
    }
  }

  const totals = sumInstallmentAmounts(sortedRows);
  const expectedInr = roundMoney(Number(netInr || 0));
  if (!moneyMatches(totals.inr, expectedInr)) {
    return `Installment amounts (INR) must total ${expectedInr}. Current total is ${totals.inr}.`;
  }

  const expectedStudent = roundMoney(Number(netStudent || 0));
  if (expectedStudent > 0 && !moneyMatches(totals.student, expectedStudent)) {
    return `Installment amounts (student currency) must total ${expectedStudent}. Current total is ${totals.student}.`;
  }

  return null;
};

const isCancelledInstallment = (row) =>
  String(row?.status ?? "").trim().toLowerCase() === "cancelled";

export const canCreateMoreFeeInstallments = (admission, allInstallments = []) => {
  const admissionId = admission?.organization_ems_admission_id;
  const maxCount = Number(admission?.installment_count || 0);
  const netFee = Number(admission?.net_fee_amount || 0);
  if (!admissionId || !Number.isFinite(maxCount) || maxCount < 1) {
    return false;
  }

  const existing = (Array.isArray(allInstallments) ? allInstallments : []).filter(
    (fi) =>
      String(fi?.organization_ems_admission_id) === String(admissionId) &&
      !isCancelledInstallment(fi)
  );

  if (existing.length >= maxCount) {
    return false;
  }

  if (netFee > 0) {
    const scheduledTotal = existing.reduce(
      (sum, fi) => sum + Number(fi?.fee_instalment_inr || 0),
      0
    );
    if (moneyMatches(scheduledTotal, netFee) || scheduledTotal >= netFee - 0.01) {
      return false;
    }
  }

  return true;
};

export const getFeeInstallmentCapacityError = ({
  admission,
  installmentNumber,
  existingInstallments = [],
  excludeInstallmentId = null,
  forCreate = true,
}) => {
  const maxCount = Number(admission?.installment_count || 0);
  const installmentNo = Number(installmentNumber || 0);
  const netFee = Number(admission?.net_fee_amount || 0);

  if (!admission?.organization_ems_admission_id) {
    return null;
  }

  if (!Number.isFinite(maxCount) || maxCount < 1) {
    return "Admission installment count is not configured.";
  }

  if (Number.isFinite(installmentNo) && installmentNo > maxCount) {
    return `Installment number cannot exceed ${maxCount} for this admission.`;
  }

  if (!forCreate) {
    return null;
  }

  const existing = (Array.isArray(existingInstallments) ? existingInstallments : []).filter(
    (fi) => {
      if (isCancelledInstallment(fi)) return false;
      if (
        excludeInstallmentId &&
        String(fi?.organization_ems_fee_installment_id) === String(excludeInstallmentId)
      ) {
        return false;
      }
      return true;
    }
  );

  if (existing.length >= maxCount) {
    return "All installments for this admission are already created.";
  }

  if (netFee > 0) {
    const scheduledTotal = existing.reduce(
      (sum, fi) => sum + Number(fi?.fee_instalment_inr || 0),
      0
    );
    if (moneyMatches(scheduledTotal, netFee) || scheduledTotal >= netFee - 0.01) {
      return "Full fee has already been collected for this admission. No additional installments can be created.";
    }
  }

  return null;
};
